import adminIds from "./analytics";
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
} from "react-router-dom";
import "./App.css";
import ScrollToTop from "./components/scrollToTop";
import { useAuth } from "./context/AuthContext";

// Login
import DiscordLoginButton from "./components/discordLoginButton";
import DiscordAuthHandler from "./components/discordAuthHandler";

// Home
import WelcomeToPogforts from "./components/pages/pogforts/home";
import HouseCup from "./components/pages/pogforts/houseCup";
import Leaderboards from "./components/pages/pogforts/leaderboards";
import Discord from "./components/pages/pogforts/discord";

// Blog / News
import BlogIndex from "./components/pages/blog/BlogIndex";
import BlogPostPage from "./components/pages/blog/BlogPostPage";
import NewsPlaceholder from "./components/pages/blog/NewsPlaceholder";
import SearchResults from "./components/pages/search/SearchResults";

// Game
import Mmm from "./components/pages/game/mmm";
import Items from "./components/pages/game/items";
import Ultimates from "./components/pages/game/ultimates";

// Bot
import MSB from "./components/pages/bot/msb";
import Commands from "./components/pages/bot/commands";
import Upvote from "./components/pages/bot/upvote";

// Legal
import About from "./components/pages/legal/about";
import PrivacyPolicy from "./components/pages/legal/privacyPolicy";
import TermsOfServices from "./components/pages/legal/termsOfServices";
import EqualityPolicy from "./components/pages/legal/equalityPolicy";

// Online
import UserProfile from "./components/pages/online/userProfile";
import Tools from "./components/pages/online/tools";
import Roulette from "./components/pages/online/roulette";
import VideoPoker from "./components/pages/online/videoPoker";
import Batrunner from "./components/pages/online/batrunner";
import BatAI from "./components/pages/online/batAI";

// Admin
import Analytics from "./components/pages/admin/analytics";
import UserDossier from "./components/pages/admin/userDossier";

// Special
import MissAyu from "./components/pages/special/missayu";

// Assets
import logo1 from "./assets/logo1.png";
import logo2 from "./assets/logo2.png";
import logo3 from "./assets/logo3.png";
import logo4 from "./assets/logo4.png";

function App() {
  return (
    <Router>
      <AppWithRouter />
    </Router>
  );
}

// Keep arcade deep links in place while the existing session is checked.
function ArcadeRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <p role="status">Checking your Pogforts session...</p>;
  }
  if (user) return children;

  const rememberGame = () => {
    try {
      window.sessionStorage.setItem("pogforts.arcadeReturn", location.pathname);
    } catch (error) {
      console.warn("Could not remember the selected arcade game:", error);
    }
  };

  return (
    <div className="arcade-login-page">
      <h1>Pogforts: Arcade</h1>
      <p>Log in with Discord to play this game.</p>
      <div onClickCapture={rememberGame}>
        <DiscordLoginButton />
      </div>
    </div>
  );
}

function PlaceholderPage({ title, description }) {
  return (
    <div>
      <h1>{title}</h1>
      <h2>{description}</h2>
    </div>
  );
}


function getDiscordAvatarUrl(user) {
  if (!user) return "";

  if (user.avatar_url) return user.avatar_url;
  if (user.avatarUrl) return user.avatarUrl;
  if (user.display_avatar_url) return user.display_avatar_url;

  if (user.id && user.avatar) {
    const extension = String(user.avatar).startsWith("a_") ? "gif" : "png";
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.${extension}?size=128`;
  }

  return "";
}

function MobileBottomNav({ user, loading, displayName, onLogout }) {
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(null);
  const avatarUrl = getDiscordAvatarUrl(user);
  const avatarFallback = String(displayName || "U").trim().charAt(0).toUpperCase() || "U";

  useEffect(() => {
    setActiveMenu(null);
  }, [location.pathname]);

  const publicSections = [
    {
      key: "pogforts",
      label: "POGFORTS",
      icon: logo1,
      items: [
        { label: "Home", to: "/" },
        { label: "House Cup", to: "/house-cup" },
        { label: "Leaderboards", to: "/leaderboards" },
        {
          label: "Discord",
          to: "/discord",
        },
      ],
    },
    {
      key: "game",
      label: "GAME",
      icon: logo3,
      items: [
        { label: "Mmm", to: "/mmm" },
        { label: "Items", to: "/items" },
        { label: "Ultimates", to: "/ultimates" },
      ],
    },
    {
      key: "bot",
      label: "BOT",
      icon: logo2,
      items: [
        { label: "Magical Sorting Bat", to: "/msb" },
        { label: "Commands", to: "/commands" },
        { label: "Upvote", to: "/upvote" },
      ],
    },
    {
      key: "legal",
      label: "LEGAL",
      icon: logo4,
      items: [
        { label: "About Pogforts", to: "/about" },
        { label: "Terms of Service", to: "/terms-of-services" },
        { label: "Privacy Policy", to: "/privacy-policy" },
        { label: "Equality Policy", to: "/equality-policy" },
      ],
    },
  ];

  const onlineSection = {
    key: "online",
    label: "ONLINE",
    items: [
      { label: displayName, to: "/online/profile" },
      { label: "Roulette", to: "/roulette" },
      { label: "Video Poker", to: "/video-poker" },
      { label: "Batrunner", to: "/batrunner" },
      { label: "Black Market", to: "/black-market" },
      { label: "Logout", action: onLogout },
    ],
  };

  const sections = user ? [...publicSections, onlineSection] : publicSections;
  const activeSection = sections.find((section) => section.key === activeMenu);

  const toggleMenu = (key) => {
    setActiveMenu((currentMenu) => (currentMenu === key ? null : key));
  };

  const closeMenu = () => {
    setActiveMenu(null);
  };

  const renderMenuItem = (item) => {
    if (item.href) {
      return (
        <a
          key={item.label}
          className="mobile-nav-slideup-link"
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMenu}
        >
          {item.label}
        </a>
      );
    }

    if (item.action) {
      return (
        <button
          key={item.label}
          type="button"
          className="mobile-nav-slideup-link mobile-nav-slideup-button"
          onClick={() => {
            closeMenu();
            item.action();
          }}
        >
          {item.label}
        </button>
      );
    }

    return (
      <Link
        key={item.label}
        className="mobile-nav-slideup-link"
        to={item.to}
        onClick={closeMenu}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
      {activeSection && (
        <div className="mobile-nav-slideup" role="menu">
          <div className="mobile-nav-slideup-title">{activeSection.label}</div>
          <div className="mobile-nav-slideup-list">
            {activeSection.items.map(renderMenuItem)}
          </div>
        </div>
      )}

      <div className="mobile-nav-bar">
        {publicSections.map((section) => (
          <button
            key={section.key}
            type="button"
            className={`mobile-nav-circle ${activeMenu === section.key ? "is-active" : ""}`}
            aria-label={section.label}
            aria-expanded={activeMenu === section.key}
            onClick={() => toggleMenu(section.key)}
          >
            <img src={section.icon} alt="" aria-hidden="true" draggable="false" />
          </button>
        ))}

        {!loading && user && (
          <button
            type="button"
            className={`mobile-nav-circle mobile-nav-avatar-button ${activeMenu === "online" ? "is-active" : ""}`}
            aria-label="Online menu"
            aria-expanded={activeMenu === "online"}
            onClick={() => toggleMenu("online")}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" aria-hidden="true" draggable="false" />
            ) : (
              <span className="mobile-nav-avatar-fallback" aria-hidden="true">
                {avatarFallback}
              </span>
            )}
          </button>
        )}

        {!loading && !user && (
          <div className="mobile-nav-login-cell">
            <DiscordLoginButton />
          </div>
        )}

        {loading && (
          <button
            type="button"
            className="mobile-nav-circle mobile-nav-loading"
            aria-label="Loading account"
            disabled
          >
            <span aria-hidden="true">…</span>
          </button>
        )}
      </div>
    </nav>
  );
}

function AppWithRouter() {
  const location = useLocation();

  const isMissAyu = location.pathname === "/missayu";
  const isAnalytics = location.pathname.startsWith("/analytics");
  const isBatrunner = location.pathname === "/batrunner";
  const [batrunnerGameActive, setBatrunnerGameActive] = useState(false);

  const operationalRoutes = [
    "/online/profile",
    "/ai",
    "/tools",
    "/roulette",
    "/video-poker",
    "/batrunner",
    "/black-market",
    "/search-results",
    "/analytics",
  ];

  const hideTicker = operationalRoutes.some(
    (route) =>
      location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  const { user, loading, logout } = useAuth();
  const displayName = user?.global_name || user?.username || "Online";
  const hideSiteChrome = isBatrunner && batrunnerGameActive;

  useEffect(() => {
    if (!isBatrunner && batrunnerGameActive) {
      setBatrunnerGameActive(false);
    }
  }, [batrunnerGameActive, isBatrunner]);

  useEffect(() => {
    const disableRightClick = (e) => {
      e.preventDefault();

      fetch("/api/log-event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "right_click_attempt",
          userId: user?.id || "guest",
          meta: {
            page: window.location.pathname,
            browser: navigator.userAgent,
          },
        }),
      }).catch((err) => console.warn("Right-click log failed:", err));
    };

    document.addEventListener("contextmenu", disableRightClick);
    return () => document.removeEventListener("contextmenu", disableRightClick);
  }, [user]);

  useEffect(() => {
    const disableKeys = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const key = String(e.key || "").toLowerCase();
      const devOverride = localStorage.getItem("devOverride") === "true";
      const isLocalhost = ["localhost", "127.0.0.1"].includes(
        window.location.hostname
      );

      if (devOverride || isLocalhost) return;

      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && key === "i") ||
        (e.ctrlKey && key === "u") ||
        (e.ctrlKey && e.shiftKey && key === "j") ||
        (e.ctrlKey && key === "s") ||
        (isMac && e.metaKey && key === "s")
      ) {
        fetch("/api/log-event", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "devtools_shortcut_block",
            userId: user?.id || "guest",
            meta: {
              page: window.location.pathname,
              key: e.key,
              browser: navigator.userAgent,
            },
          }),
        }).catch((err) => console.warn("Devtools shortcut log failed:", err));

        e.preventDefault();
        console.warn("🛡️ Developer shortcut blocked.");
      }
    };

    window.addEventListener("keydown", disableKeys);
    return () => window.removeEventListener("keydown", disableKeys);
  }, [user]);

  useEffect(() => {
    fetch("/api/log-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "page_view",
        userId: user?.id || "guest",
        meta: {
          page: location.pathname,
          browser: navigator.userAgent,
        },
      }),
    }).catch((err) => console.warn("Page view log failed:", err));
  }, [location.pathname, user]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.warn("Logout request failed:", error);
    } finally {
      window.location.href = "/";
    }
  };

  if (isMissAyu) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/missayu" element={<MissAyu />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />

      <div
        className={`app-container ${isAnalytics ? "analytics-layout" : ""} ${
          hideTicker ? "no-ticker" : ""
        } ${hideSiteChrome ? "batrunner-game-mode" : ""}`}
      >
        {!hideTicker && !hideSiteChrome && (
          <div className="ticker">
            <p>
              🛈 ⯄ &nbsp;&nbsp;&nbsp;&nbsp;
              Experience a Discord server game like never before; join Pogforts
              to play Murder Mystery Magic.
              &nbsp;&nbsp;&nbsp;&nbsp; 🛈 ⯄ &nbsp;&nbsp;&nbsp;&nbsp;
              Ruthless Social Strategy RPG comes to life.
              &nbsp;&nbsp;&nbsp;&nbsp; 🛈 ⯄ &nbsp;&nbsp;&nbsp;&nbsp;
              Inspired by Bob Kane, HP Lovecraft, JK Rowling, and Manly P Hall.
              &nbsp;&nbsp;&nbsp;&nbsp; 🛈 ⯄ &nbsp;&nbsp;&nbsp;&nbsp;
              The Worlds Most Dangerous Discord.
              &nbsp;&nbsp;&nbsp;&nbsp; 🛈 ⯄ &nbsp;&nbsp;&nbsp;&nbsp;
              NOW RECRUITING!!!
            </p>
          </div>
        )}

        {!hideSiteChrome && <nav className="sidebar">
          <div className="sidebar-logos">
            <img src={logo1} alt="Logo 1" />
            <img src={logo2} alt="Logo 2" />
            <img src={logo3} alt="Logo 3" />
            <img src={logo4} alt="Logo 4" />
          </div>

          <h1>POGFORTS</h1>
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/house-cup">House Cup</Link></li>
            <li><Link to="/leaderboards">Leaderboards</Link></li>
            <li><Link to="/discord">Discord</Link></li>
          </ul>

          <h1>GAME</h1>
          <ul>
            <li><Link to="/mmm">Mmm</Link></li>
            <li><Link to="/items">Items</Link></li>
            <li><Link to="/ultimates">Ultimates</Link></li>
          </ul>

          <h1>BOT</h1>
          <ul>
            <li><Link to="/msb">Magical Sorting Bat</Link></li>
            <li><Link to="/commands">Commands</Link></li>
            <li><Link to="/upvote">Upvote</Link></li>
          </ul>

          <h1>LEGAL</h1>
          <ul>
            <li><Link to="/about">About Pogforts</Link></li>
            <li><Link to="/terms-of-services">Terms of Services</Link></li>
            <li><Link to="/privacy-policy">Privacy Policy</Link></li>
            <li><Link to="/equality-policy">Equality Policy</Link></li>
          </ul>

          {!loading && user && (
            <>
              <h1>ONLINE</h1>
              <ul>
                <li><Link to="/online/profile">{displayName}</Link></li>
                <li><Link to="/ai">Bat AI</Link></li>
                <li><Link to="/roulette">Roulette</Link></li>
                <li><Link to="/video-poker">Video Poker</Link></li>
                <li><Link to="/batrunner">Batrunner</Link></li>
                <li><Link to="/black-market">Black Market</Link></li>
                <li>
                  <button
                    type="button"
                    className="sidebar-link-button"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </li>
              </ul>
            </>
          )}

          {!loading && !user && <DiscordLoginButton />}
        </nav>}

        <div className={`main-content ${hideSiteChrome ? "batrunner-main-content" : ""}`}>
          <Routes>
            <Route path="/" element={<WelcomeToPogforts />} />
            <Route path="/discord" element={<Discord />} />
            <Route path="/house-cup" element={<HouseCup />} />
            <Route path="/leaderboards" element={<Leaderboards />} />

            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/news" element={<NewsPlaceholder />} />
            <Route path="/search-results" element={<SearchResults />} />

            <Route path="/mmm" element={<Mmm />} />
            <Route path="/items" element={<Items />} />
            <Route path="/ultimates" element={<Ultimates />} />

            <Route path="/msb" element={<MSB />} />
            <Route path="/commands" element={<Commands />} />
            <Route path="/upvote" element={<Upvote />} />

            <Route path="/about" element={<About />} />
            <Route path="/terms-of-services" element={<TermsOfServices />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/equality-policy" element={<EqualityPolicy />} />

            <Route
              path="/online/profile"
              element={!loading && user ? <UserProfile /> : <Navigate to="/" replace />}
            />

            <Route
              path="/ai"
              element={!loading && user ? <BatAI /> : <Navigate to="/" replace />}
            />

            <Route
              path="/tools"
              element={!loading && user ? <Tools /> : <Navigate to="/" replace />}
            />

            <Route
              path="/roulette"
              element={<ArcadeRoute><Roulette /></ArcadeRoute>}
            />

            <Route
              path="/video-poker"
              element={<ArcadeRoute><VideoPoker /></ArcadeRoute>}
            />

            <Route
              path="/batrunner"
              element={
                <ArcadeRoute>
                  <Batrunner onGameModeChange={setBatrunnerGameActive} />
                </ArcadeRoute>
              }
            />

            <Route
              path="/black-market"
              element={
                !loading && user ? (
                  <PlaceholderPage
                    title="Black Market"
                    description="The Black Market page structure is now locked in for the ONLINE section."
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route path="/auth/discord/callback" element={<DiscordAuthHandler />} />

            <Route
              path="/analytics"
              element={
                !loading && user && adminIds.includes(user.id) ? (
                  <Analytics />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/analytics/user/:id"
              element={
                !loading && user && adminIds.includes(user.id) ? (
                  <UserDossier />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </div>

        {!hideSiteChrome && (
          <MobileBottomNav
            user={user}
            loading={loading}
            displayName={displayName}
            onLogout={handleLogout}
          />
        )}
      </div>
    </>
  );
}

export default App;
